import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Star } from "lucide-react";
import { API } from "@/lib/api";
import StripeBar from "@/components/StripeBar";
import { SLOGAN } from "@/lib/data";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        // keep polling
      }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <main className="bg-navy min-h-[70vh] flex items-center" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 w-full">
        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden text-center p-8 sm:p-12">
          <StripeBar className="absolute top-0 inset-x-0 h-2.5" />
          {state === "checking" && (
            <div data-testid="payment-confirming">
              <Loader2 className="w-14 h-14 text-crimson animate-spin mx-auto mb-6 mt-4" />
              <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy mb-3">
                Confirming your contribution…
              </h1>
              <p className="text-navy/60 text-base">One moment while we verify your payment.</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-paid-message">
              <CheckCircle2 className="w-16 h-16 text-crimson mx-auto mb-6 mt-4" />
              <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy mb-4">
                Thank You for Your Contribution!
              </h1>
              <p className="text-navy/70 text-base sm:text-lg leading-relaxed mb-3">
                Your contribution to Dave Graham's campaign has been received. You're directly helping reach voters across Delaware.
              </p>
              <p className="text-navy/55 text-sm leading-relaxed mb-8">
                A receipt will be sent to your email. Contributions are not tax deductible. Paid for by Graham of Delaware, LLC.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/" data-testid="success-home-button" className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-lg px-8 py-3.5 rounded-full transition-colors min-h-[52px]">
                  Back to Home
                </Link>
                <Link to="/get-involved" data-testid="success-involved-button" className="inline-flex items-center gap-2 border-2 border-navy/30 hover:border-navy text-navy font-display font-extrabold uppercase tracking-wider text-lg px-8 py-3.5 rounded-full transition-colors min-h-[52px]">
                  Get Involved
                </Link>
              </div>
            </div>
          )}
          {state === "pending" && (
            <div data-testid="payment-pending-message">
              <Loader2 className="w-14 h-14 text-gold mx-auto mb-6 mt-4" />
              <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy mb-4">
                Payment Processing
              </h1>
              <p className="text-navy/70 text-base sm:text-lg leading-relaxed">
                Your payment is being processed. You'll receive an email receipt once it completes — no further action is needed.
              </p>
            </div>
          )}
          {state === "error" && (
            <div data-testid="payment-error-message">
              <XCircle className="w-14 h-14 text-crimson mx-auto mb-6 mt-4" />
              <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy mb-4">
                Something's Missing
              </h1>
              <p className="text-navy/70 text-base leading-relaxed mb-8">
                We couldn't find this payment session. If you completed a contribution, it was received — check your email for a receipt.
              </p>
              <Link to="/contribute" data-testid="error-back-button" className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-lg px-8 py-3.5 rounded-full transition-colors min-h-[52px]">
                Back to Contribute
              </Link>
            </div>
          )}
          <p className="flex items-center justify-center gap-2 font-editorial font-semibold text-navy/50 text-sm mt-10">
            <Star className="w-3.5 h-3.5 text-crimson fill-crimson" /> {SLOGAN}
          </p>
        </div>
      </div>
    </main>
  );
};

export default PaymentSuccess;
